/**
 * Admin-managed calendars — pure model, column and filter helpers.
 *
 * Every calendar follows one of three rules (mirrors healo_backend storage.Calendar):
 *   - blocking (default)          — joins its doctor's shared busy pool;
 *   - non-blocking (nonBlocking)  — fully independent of the doctor's other calendars;
 *   - standalone (no doctor)      — a named calendar with no doctor (and normally no clinic),
 *                                    always isolated, filled by direct entries and rendered as
 *                                    its own day-view column.
 * `localOnly` calendars are never pushed to the Plato EMR.
 *
 * Dependency-free on purpose: vitest runs in node with no DOM, so everything the
 * Settings editor and the Calendar page need to reason about lives here and is tested.
 */

export const NIL_OBJECT_ID = "000000000000000000000000";

const CALENDAR_STATUSES = new Set(["active", "inactive"]);
const CALENDAR_ORIGINS = new Set(["plato", "local"]);
export const CALENDAR_NAME_MAX_LENGTH = 80;
export const CALENDAR_COLUMN_PREFIX = "cal:";

/** True for "", whitespace or the all-zero ObjectID sentinel. */
export function isNilId(id) {
  const value = String(id ?? "").trim();
  return value === "" || value === NIL_OBJECT_ID;
}

function cleanId(id) {
  return isNilId(id) ? "" : String(id).trim();
}

function toInt(value) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? Math.trunc(n) : 0;
}

export function usageCounts(usage) {
  const source = usage && typeof usage === "object" ? usage : {};
  return {
    slots: toInt(source.slots),
    sessionBlocks: toInt(source.sessionBlocks),
    upcomingAppointments: toInt(source.upcomingAppointments),
  };
}

/**
 * Normalize a calendar from either `/api/admin/calendars` or `/api/calendar/calendars`
 * into one shape. Nil doctor/tenant ids become "", unknown status/origin fall back.
 */
export function normalizeCalendar(raw) {
  const source = raw && typeof raw === "object" ? raw : {};
  const status = String(source.status || "").trim().toLowerCase();
  const origin = String(source.origin || "").trim().toLowerCase();
  const metadata = source.metadata && typeof source.metadata === "object" ? source.metadata : {};
  const migrationSource = String(metadata.migrationSource || "").trim();
  return {
    id: String(source.id || source._id || "").trim(),
    name: String(source.name || "").trim(),
    doctorId: cleanId(source.doctorId),
    tenantId: cleanId(source.tenantId),
    timezone: String(source.timezone || "").trim(),
    status: CALENDAR_STATUSES.has(status) ? status : "active",
    nonBlocking: source.nonBlocking === true || source.nonBlocking === "true",
    localOnly: source.localOnly === true || source.localOnly === "true",
    origin: CALENDAR_ORIGINS.has(origin)
      ? origin
      : migrationSource === "plato.appointment_calendars"
        ? "plato"
        : "local",
    colorHex: String(source.colorHex || metadata.colorHex || "").trim(),
    exceptions: Array.isArray(source.exceptions) ? source.exceptions : [],
    usage: usageCounts(source.usage),
  };
}

export function makeCalendarDraft({ timezone = "" } = {}) {
  return {
    name: "",
    doctorId: "",
    tenantId: "",
    timezone: String(timezone || "").trim(),
    status: "active",
    nonBlocking: false,
    localOnly: false,
  };
}

/** The POST/PUT body for a draft (trimmed ids, coerced booleans). */
export function normalizeCalendarDraft(draft) {
  const source = draft && typeof draft === "object" ? draft : {};
  const status = String(source.status || "").trim().toLowerCase();
  return {
    name: String(source.name || "").trim(),
    doctorId: cleanId(source.doctorId),
    tenantId: cleanId(source.tenantId),
    timezone: String(source.timezone || "").trim(),
    status: CALENDAR_STATUSES.has(status) ? status : "active",
    nonBlocking: Boolean(source.nonBlocking),
    localOnly: Boolean(source.localOnly),
  };
}

function isValidTimezone(tz) {
  const value = String(tz || "").trim();
  if (!value) return false;
  try {
    // eslint-disable-next-line no-new
    new Intl.DateTimeFormat("en-US", { timeZone: value });
    return true;
  } catch {
    return false;
  }
}

/** Errors map ({} when valid). */
export function validateCalendarDraft(draft) {
  const body = normalizeCalendarDraft(draft);
  const errors = {};
  if (!body.name) {
    errors.name = "Calendar name is required.";
  } else if (body.name.length > CALENDAR_NAME_MAX_LENGTH) {
    errors.name = `Keep the name under ${CALENDAR_NAME_MAX_LENGTH} characters.`;
  }
  if (!isValidTimezone(body.timezone)) {
    errors.timezone = "Pick a valid timezone.";
  }
  return errors;
}

export function isPlatoCalendar(cal) {
  return String(cal?.origin || "").trim().toLowerCase() === "plato";
}

/** Fields an editor must lock: everything but the blocking rule for Plato calendars. */
export function lockedFields(cal) {
  if (!isPlatoCalendar(cal)) return new Set();
  return new Set(["name", "doctorId", "tenantId", "timezone", "status", "localOnly", "delete"]);
}

/**
 * PUT body: a Plato calendar gets the SAVED document plus the new blocking rule, so a stale
 * or whitespace-different name never trips the server's 409; local calendars send the draft.
 */
export function buildCalendarUpdateBody(saved, draft) {
  if (isPlatoCalendar(saved)) {
    return { ...normalizeCalendarDraft(saved), nonBlocking: Boolean(draft?.nonBlocking) };
  }
  return normalizeCalendarDraft(draft);
}

export function isStandaloneCalendar(cal) {
  return isNilId(cal?.doctorId);
}

/** "doctor" | "clinic" | "standalone" */
export function calendarKind(cal) {
  if (!isNilId(cal?.doctorId)) return "doctor";
  if (!isNilId(cal?.tenantId)) return "clinic";
  return "standalone";
}

export function calendarKindLabel(cal, { doctorName = "", clinicName = "" } = {}) {
  switch (calendarKind(cal)) {
    case "doctor":
      return String(doctorName || "").trim() || "Doctor";
    case "clinic":
      return `${String(clinicName || "").trim() || "Clinic"} (clinic)`;
    default:
      return "Standalone";
  }
}

/**
 * Standalone calendars (no doctor at all) sorted by name, each with a deterministic
 * `colorHex` (an explicit calendar colour wins over the hashed hue).
 */
export function standaloneCalendars(items, { activeOnly = true } = {}) {
  return (Array.isArray(items) ? items : [])
    .map(normalizeCalendar)
    .filter((cal) => cal.id && isNilId(cal.doctorId) && (!activeOnly || cal.status === "active"))
    .map((cal) => ({ ...cal, colorHex: cal.colorHex || hashHueHex(cal.id) }))
    .sort((a, b) => a.name.localeCompare(b.name) || a.id.localeCompare(b.id));
}

function plural(n, singular, pluralForm = `${singular}s`) {
  return `${n} ${n === 1 ? singular : pluralForm}`;
}

/** "12 slots · 3 session blocks · 5 upcoming appointments" */
export function usageSummary(usage) {
  const u = usageCounts(usage);
  return [
    plural(u.slots, "slot"),
    plural(u.sessionBlocks, "session block"),
    plural(u.upcomingAppointments, "upcoming appointment"),
  ].join(" · ");
}

/** `/api/users/doctors` items → `[{id, label}]` ("First Last" → displayName → id). */
export function doctorsToOptions(items) {
  return (Array.isArray(items) ? items : [])
    .map((doctor) => {
      const id = String(doctor?.id || doctor?._id || "").trim();
      if (!id) return null;
      const profile = doctor?.profile && typeof doctor.profile === "object" ? doctor.profile : doctor || {};
      const first = String(profile.firstName || "").trim();
      const last = String(profile.lastName || "").trim();
      const full = [first, last].filter(Boolean).join(" ");
      const label = full || String(profile.displayName || doctor?.displayName || "").trim() || id;
      return { id, label };
    })
    .filter(Boolean)
    .sort((a, b) => a.label.localeCompare(b.label));
}

/** Picker label with the rule hints: "Name · non-blocking · local only". */
export function calendarOptionLabel(cal) {
  const source = cal && typeof cal === "object" ? cal : {};
  const name = String(source.name || source.title || source.id || source._id || "").trim();
  const hints = [];
  if (source.nonBlocking === true) hints.push("non-blocking");
  if (source.localOnly === true) hints.push("local only");
  return hints.length ? `${name} · ${hints.join(" · ")}` : name;
}

// ── Colour ────────────────────────────────────────────────────────────────────

function clamp255(value) {
  return Math.max(0, Math.min(255, Math.round(value)));
}

function rgbToHex({ r, g, b }) {
  return `#${[r, g, b].map((v) => clamp255(v).toString(16).padStart(2, "0")).join("")}`;
}

function hslToHex(h, s, l) {
  const hue = (((h % 360) + 360) % 360) / 360;
  if (s === 0) {
    const v = clamp255(l * 255);
    return rgbToHex({ r: v, g: v, b: v });
  }
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const channel = (t) => {
    let tn = t;
    if (tn < 0) tn += 1;
    if (tn > 1) tn -= 1;
    if (tn < 1 / 6) return p + (q - p) * 6 * tn;
    if (tn < 1 / 2) return q;
    if (tn < 2 / 3) return p + (q - p) * (2 / 3 - tn) * 6;
    return p;
  };
  return rgbToHex({
    r: channel(hue + 1 / 3) * 255,
    g: channel(hue) * 255,
    b: channel(hue - 1 / 3) * 255,
  });
}

/**
 * Deterministic, pleasant hue for an entity with no calendar colour of its own
 * (clinics in the session rail, standalone calendars). Same string → same colour.
 */
export function hashHueHex(value) {
  const str = String(value || "");
  let hash = 0;
  for (let i = 0; i < str.length; i += 1) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash |= 0;
  }
  const hue = Math.abs(hash) % 360;
  return hslToHex(hue, 0.62, 0.5);
}

// ── Calendar page: columns, filtering, DnD ────────────────────────────────────

export function calendarColumnId(calendarId) {
  return `${CALENDAR_COLUMN_PREFIX}${String(calendarId || "").trim()}`;
}

export function isCalendarColumnId(columnId) {
  return typeof columnId === "string" && columnId.startsWith(CALENDAR_COLUMN_PREFIX) && columnId.length > CALENDAR_COLUMN_PREFIX.length;
}

export function parseCalendarColumnId(columnId) {
  return isCalendarColumnId(columnId) ? columnId.slice(CALENDAR_COLUMN_PREFIX.length) : null;
}

export function isDirectEntryEvent(event) {
  return event?.directEntry === true || event?.metadata?.directEntry === true;
}

/**
 * A standalone-calendar event: its calendar is in the standalone set, OR it is a direct
 * entry, OR it carries a calendarId with neither doctor nor tenant. The fallbacks keep
 * columns right while the calendars index is still loading.
 */
export function isStandaloneCalendarEvent(event, standaloneIdSet) {
  if (!event) return false;
  const calendarId = cleanId(event.calendarId);
  if (calendarId && standaloneIdSet && typeof standaloneIdSet.has === "function" && standaloneIdSet.has(calendarId)) {
    return true;
  }
  if (isDirectEntryEvent(event)) return true;
  return Boolean(calendarId) && isNilId(event.doctorId) && isNilId(event.tenantId);
}

function columnDescriptor(column) {
  if (column && typeof column === "object") {
    const id = String(column.id || "").trim();
    const kind = column.kind || (id === "_all" ? "all" : isCalendarColumnId(id) ? "calendar" : "entity");
    return { id, kind, calendarId: column.calendarId || parseCalendarColumnId(id) || "" };
  }
  const id = String(column || "").trim();
  return { id, kind: id === "_all" ? "all" : isCalendarColumnId(id) ? "calendar" : "entity", calendarId: parseCalendarColumnId(id) || "" };
}

/**
 * Column matching: a calendar column takes exactly its calendar's events; entity
 * (doctor/clinic) columns and the untitled "_all" column never take standalone events.
 */
export function eventMatchesColumn(event, column, groupBy = "tenant", standaloneIdSet) {
  const col = columnDescriptor(column);
  const standalone = isStandaloneCalendarEvent(event, standaloneIdSet);
  if (col.kind === "calendar") {
    return Boolean(col.calendarId) && cleanId(event?.calendarId) === col.calendarId;
  }
  if (standalone) return false;
  if (col.kind === "all") return true;
  const field = groupBy === "doctor" ? "doctorId" : "tenantId";
  return cleanId(event?.[field]) === col.id;
}

/**
 * Day-view columns: the entity (doctor or clinic) selection first, then one column per
 * selected standalone calendar. `null` when nothing is selected at all (one untitled
 * column). When the entity facet has no loaded options (fetch pending/failed) but
 * calendar columns exist, an explicit "_all" pool column keeps every doctor/clinic
 * event visible instead of hiding it behind the calendar columns.
 */
export function buildDayViewColumns({ groupBy = "tenant", selectedDoctors = [], selectedTenants = [], selectedCalendars = [], entityFacetLoaded = true } = {}) {
  const entities = (groupBy === "doctor" ? selectedDoctors : selectedTenants) || [];
  const entityColumns = entities
    .map((e) => ({ id: String(e?.id || "").trim(), name: e?.name, kind: "entity" }))
    .filter((c) => c.id);
  const calendarColumns = (selectedCalendars || [])
    .map((c) => {
      const calendarId = String(c?.id || "").trim();
      if (!calendarId) return null;
      return { id: calendarColumnId(calendarId), name: c?.name || "Calendar", kind: "calendar", calendarId, colorHex: c?.colorHex || "" };
    })
    .filter(Boolean);
  if (entityColumns.length === 0 && calendarColumns.length === 0) return null;
  if (entityColumns.length === 0 && !entityFacetLoaded) {
    return [{ id: "_all", name: null, kind: "all" }, ...calendarColumns];
  }
  return [...entityColumns, ...calendarColumns];
}

function defaultIsCancelled(event) {
  return String(event?.status || "").trim().toLowerCase() === "cancelled";
}

/**
 * The pure body of the page's `filteredEvents`: standalone events pass iff their calendar
 * is selected (they bypass clinic/doctor membership entirely); every other event keeps the
 * existing tenant + doctor membership rule; cancelled events drop last unless shown.
 */
export function filterCalendarEvents(events, {
  tenantFilterEnabled = false,
  tenantIdSet = new Set(),
  doctorFilterEnabled = false,
  doctorIdSet = new Set(),
  calendarIdSet = new Set(),
  standaloneIdSet = new Set(),
  showCancelled = false,
  isCancelled = defaultIsCancelled,
} = {}) {
  const list = Array.isArray(events) ? events : [];
  return list.filter((event) => {
    if (isStandaloneCalendarEvent(event, standaloneIdSet)) {
      const calendarId = cleanId(event?.calendarId);
      if (!calendarId || !calendarIdSet.has(calendarId)) return false;
    } else {
      if (tenantFilterEnabled && !tenantIdSet.has(cleanId(event?.tenantId))) return false;
      if (doctorFilterEnabled && !doctorIdSet.has(cleanId(event?.doctorId))) return false;
    }
    if (!showCancelled && isCancelled(event)) return false;
    return true;
  });
}

/**
 * DnD gates. Standalone entries only ever move in time: they may land in their own
 * calendar column or a week/month day cell, never on a doctor/clinic column, the
 * untitled column or an open slot. Doctor/clinic appointments never land on a
 * calendar column.
 */
export function canDropOnColumn(dragEvent, columnData, standaloneIdSet) {
  const standalone = isStandaloneCalendarEvent(dragEvent, standaloneIdSet);
  const kind = columnData?.kind || (columnData?.columnId == null ? "day" : columnDescriptor(columnData.columnId).kind);
  if (kind === "calendar") {
    const calendarId = String(columnData?.calendarId || parseCalendarColumnId(columnData?.columnId) || "").trim();
    return Boolean(calendarId) && cleanId(dragEvent?.calendarId) === calendarId;
  }
  if (kind === "day") return true; // week/month day cell: time-only moves
  return !standalone;
}

export function canDropOnSlot(dragEvent, standaloneIdSet) {
  return !isStandaloneCalendarEvent(dragEvent, standaloneIdSet);
}

// ── Direct entries ────────────────────────────────────────────────────────────

export function minutesToHhmm(minutes) {
  const total = Math.max(0, Math.min(24 * 60, Math.round(Number(minutes) || 0)));
  const h = Math.floor(total / 60);
  const m = total % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

function hhmmToMinutes(value) {
  const match = /^(\d{1,2}):(\d{2})$/.exec(String(value || "").trim());
  if (!match) return null;
  const h = Number(match[1]);
  const m = Number(match[2]);
  if (h < 0 || h > 24 || m < 0 || m > 59 || (h === 24 && m > 0)) return null;
  return h * 60 + m;
}

/**
 * Snap a clicked minute to the grid and clamp the default duration inside the visible
 * window. Returns `{startMinutes, endMinutes}`.
 */
export function directEntryRangeFromMinute(minute, { snapMinutes = 15, durationMinutes = 30, startMinutes = 0, endMinutes = 24 * 60 } = {}) {
  const snap = Math.max(1, Math.round(snapMinutes));
  const duration = Math.max(snap, Math.round(durationMinutes));
  const windowStart = Math.max(0, Math.round(startMinutes));
  const windowEnd = Math.min(24 * 60, Math.max(windowStart + snap, Math.round(endMinutes)));
  let start = Math.floor((Number(minute) || 0) / snap) * snap;
  start = Math.max(windowStart, start);
  if (start + duration > windowEnd) {
    start = Math.max(windowStart, windowEnd - duration);
  }
  const end = Math.min(windowEnd, start + duration);
  return { startMinutes: start, endMinutes: end };
}

export function makeCalendarEntryForm({ date = "", startTime = "", endTime = "" } = {}) {
  return {
    title: "",
    date: String(date || "").trim(),
    startTime: String(startTime || "").trim(),
    endTime: String(endTime || "").trim(),
    patientId: "",
    description: "",
    colorHex: "",
  };
}

export function validateCalendarEntryForm(form) {
  const source = form && typeof form === "object" ? form : {};
  const errors = {};
  if (!String(source.title || "").trim()) errors.title = "Give the entry a title.";
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(source.date || "").trim())) errors.date = "Pick a date.";
  const start = hhmmToMinutes(source.startTime);
  const end = hhmmToMinutes(source.endTime);
  if (start == null) errors.startTime = "Start time must be HH:MM.";
  if (end == null) errors.endTime = "End time must be HH:MM.";
  if (start != null && end != null) {
    if (end <= start) errors.endTime = "End must be after start.";
    else if (end - start < 15) errors.endTime = "Entries are at least 15 minutes.";
  }
  return errors;
}

/** API body for POST /api/admin/calendars/{id}/entries (empty optionals omitted). */
export function buildCalendarEntryBody(form) {
  const source = form && typeof form === "object" ? form : {};
  const body = {
    title: String(source.title || "").trim(),
    date: String(source.date || "").trim(),
    startTime: String(source.startTime || "").trim(),
    endTime: String(source.endTime || "").trim(),
  };
  const patientId = cleanId(source.patientId);
  if (patientId) body.patientId = patientId;
  const description = String(source.description || "").trim();
  if (description) body.description = description;
  const colorHex = String(source.colorHex || "").trim();
  if (colorHex) body.colorHex = colorHex;
  return body;
}

/**
 * Saved-filter reconciliation for the Calendars facet: `null` (legacy preset) means
 * "all standalone calendars"; an array is intersected with the loaded options, but kept
 * verbatim when no options loaded (mirrors applySavedFilter's rule for clinics/doctors).
 */
export function resolveSavedCalendarIds(savedIds, options) {
  const optionIds = (Array.isArray(options) ? options : []).map((o) => String(o?.id || "").trim()).filter(Boolean);
  if (!Array.isArray(savedIds)) return optionIds;
  const wanted = savedIds.map((id) => String(id || "").trim()).filter(Boolean);
  if (optionIds.length === 0) return wanted;
  const allowed = new Set(optionIds);
  return wanted.filter((id) => allowed.has(id));
}

// ── Settings → Calendars editor helpers ──────────────────────────────────────

/** Label of `{id, label}` option `id`, or `fallback` ("" by default) when unknown. */
export function lookupOptionLabel(options, id, fallback = "") {
  const key = String(id || "").trim();
  if (!key) return fallback;
  const found = (Array.isArray(options) ? options : []).find((o) => String(o?.id || "") === key);
  return found ? String(found.label || "").trim() || fallback : fallback;
}

/**
 * The Settings list's three sections. Doctor calendars sort by doctor label, then name;
 * clinic calendars by clinic label, then name; standalone by name. Items are normalized on
 * the way in so raw API rows and already-normalized calendars both work.
 */
export function groupCalendarsForSettings(items, { doctorOptions = [], clinicOptions = [] } = {}) {
  const groups = { doctor: [], clinic: [], standalone: [] };
  for (const raw of Array.isArray(items) ? items : []) {
    const cal = normalizeCalendar(raw);
    if (!cal.id) continue;
    groups[calendarKind(cal)].push(cal);
  }
  const byName = (a, b) => a.name.localeCompare(b.name) || a.id.localeCompare(b.id);
  const byOwner = (options, field) => (a, b) =>
    lookupOptionLabel(options, a[field], a[field]).localeCompare(lookupOptionLabel(options, b[field], b[field])) || byName(a, b);
  groups.doctor.sort(byOwner(doctorOptions, "doctorId"));
  groups.clinic.sort(byOwner(clinicOptions, "tenantId"));
  groups.standalone.sort(byName);
  return groups;
}

/** Dirty check for the editor: both sides pass through normalizeCalendarDraft first. */
export function calendarDraftsEqual(a, b) {
  return JSON.stringify(normalizeCalendarDraft(a)) === JSON.stringify(normalizeCalendarDraft(b));
}

/**
 * Delete-confirm copy. `usage` (a fresh GET) wins over the calendar's own counts. Standalone
 * calendars hold direct entries only, and DELETE cancels the future ones.
 */
export function deleteCalendarPreview(cal, usage) {
  const u = usageCounts(usage ?? cal?.usage);
  return {
    removed: `${plural(u.slots, "slot")}, ${plural(u.sessionBlocks, "session block")}`,
    kept: plural(u.upcomingAppointments, "upcoming appointment"),
    cancelsDirectEntries: isStandaloneCalendar(cal),
  };
}

/** Toast line for a DELETE response's `cascade` (null/empty body → the plain confirmation). */
export function deleteCascadeSummary(cascade) {
  if (!cascade || typeof cascade !== "object") return "Calendar deleted.";
  const slots = toInt(cascade.slots);
  const blocks = toInt(cascade.sessionBlocks);
  const cancelled = toInt(cascade.directEntriesCancelled);
  const retained = toInt(cascade.appointmentsRetained);
  const parts = [`removed ${plural(slots, "slot")} and ${plural(blocks, "session block")}`];
  if (cancelled > 0) parts.push(`cancelled ${plural(cancelled, "direct entry", "direct entries")}`);
  if (retained > 0) parts.push(`kept ${plural(retained, "appointment")}`);
  const sentence = parts.join("; ");
  return `Calendar deleted — ${sentence}.`;
}

/** "Asia/Singapore" → "Asia/Singapore" shown as "Asia/Singapore"; underscores read as spaces. */
export function prettyTimeZone(zone) {
  return String(zone || "").trim().replace(/_/g, " ");
}

/** "GMT+8" style offset of `zone` at `date` ("" for an unknown zone). */
export function timeZoneOffsetLabel(zone, date = new Date()) {
  const value = String(zone || "").trim();
  if (!value) return "";
  try {
    const parts = new Intl.DateTimeFormat("en-US", { timeZone: value, timeZoneName: "shortOffset" }).formatToParts(date);
    const part = parts.find((p) => p.type === "timeZoneName");
    const label = part ? String(part.value || "").trim() : "";
    // Node's ICU prints "GMT+0" where browsers print "GMT"; one spelling for both.
    return /^GMT[+-]0(:00)?$/.test(label) ? "GMT" : label;
  } catch {
    return "";
  }
}
